import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Tooltip,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { PiMicrosoftExcelLogo } from 'react-icons/pi';
import { MdPictureAsPdf } from 'react-icons/md';
import { utils, writeFile } from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { config } from './CustomComponents/config';

const UnassignedParentProductsDialog = ({ open, onClose }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const fetchUnassignedProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${config.Api}ParentProduct/getUnassignedParentProducts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setProducts(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to fetch unassigned parent products');
      }
    } catch (err) {
      console.error('Error fetching unassigned parent products:', err);
      setError(err.message || 'Failed to fetch unassigned parent products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUnassignedProducts();
      setPage(0);
    }
  }, [open]);

  // Filter products by search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    const query = searchQuery.toLowerCase().trim();
    return products.filter((p) => {
      const code = (p.parentProductCode || '').toLowerCase();
      const name = (p.parentProductName || '').toLowerCase();
      return code.includes(query) || name.includes(query);
    });
  }, [products, searchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export to Excel
  const handleExportExcel = () => {
    try {
      setExportingExcel(true);
      const dataToExport = filteredProducts.length > 0 ? filteredProducts : products;
      if (dataToExport.length === 0) {
        alert('No data to export');
        return;
      }

      const rows = dataToExport.map((p, index) => ({
        'S.No': index + 1,
        'Parent Product Code': p.parentProductCode || '',
        'Parent Product Name': p.parentProductName || ''
      }));

      const worksheet = utils.json_to_sheet(rows);
      // Auto-fit column widths
      const colWidths = [
        { wch: 8 },
        { wch: 25 },
        { wch: 55 }
      ];
      worksheet['!cols'] = colWidths;

      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, 'Unconfigured Products');

      const dateStr = new Date().toISOString().slice(0, 10);
      writeFile(workbook, `Unconfigured_Parent_Products_${dateStr}.xlsx`);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Failed to export to Excel: ' + err.message);
    } finally {
      setExportingExcel(false);
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    try {
      setExportingPdf(true);
      const dataToExport = filteredProducts.length > 0 ? filteredProducts : products;
      if (dataToExport.length === 0) {
        alert('No data to export');
        return;
      }

      const doc = new jsPDF('portrait', 'mm', 'a4');
      const dateStr = new Date().toLocaleString('en-GB');

      // Title & Header Information
      doc.setFontSize(16);
      doc.setTextColor(24, 60, 108);
      doc.setFont('helvetica', 'bold');
      doc.text('GOLDSUN AUTO - UNCONFIGURED PARENT PRODUCTS', 14, 15);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Generated on: ${dateStr} | Total Records: ${dataToExport.length}`,
        14,
        22
      );

      const headers = [
        ['S.No', 'Parent Product Code', 'Parent Product Name']
      ];

      const bodyData = dataToExport.map((p, index) => [
        index + 1,
        p.parentProductCode || '-',
        p.parentProductName || '-'
      ]);

      autoTable(doc, {
        head: headers,
        body: bodyData,
        startY: 26,
        theme: 'striped',
        headStyles: {
          fillColor: [24, 60, 108],
          textColor: [255, 255, 255],
          fontSize: 10,
          fontStyle: 'bold',
          halign: 'left'
        },
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: 'linebreak',
          lineColor: [220, 220, 220],
          lineWidth: 0.1
        },
        columnStyles: {
          0: { cellWidth: 16, halign: 'center' },
          1: { cellWidth: 50, fontStyle: 'bold' },
          2: { cellWidth: 'auto' }
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(140);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.width - 25,
            doc.internal.pageSize.height - 8
          );
        }
      });

      const fileDate = new Date().toISOString().slice(0, 10);
      doc.save(`Unconfigured_Parent_Products_${fileDate}.pdf`);
    } catch (err) {
      console.error('Error exporting to PDF:', err);
      alert('Failed to export to PDF: ' + err.message);
    } finally {
      setExportingPdf(false);
    }
  };

  const displayedRows = filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '70vh',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f5f7fa',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Inventory2OutlinedIcon sx={{ color: '#1976d2', fontSize: 28 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                Unconfigured Parent Products
              </Typography>
              <Chip
                label={`Total: ${products.length}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#718096' }}>
              Parent products not configured with any Main Parent product
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#4a5568' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Toolbar / Search / Export Actions */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fafbfc',
          borderBottom: '1px solid #edf2f7'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 260 }}>
          <TextField
            size="small"
            placeholder="Search by code or product name..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: '#a0aec0' }} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 360, width: '100%', bgcolor: 'white' }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title="Refresh List">
            <span>
              <IconButton
                onClick={fetchUnassignedProducts}
                disabled={loading}
                size="small"
                sx={{
                  border: '1px solid #cbd5e0',
                  borderRadius: 1,
                  bgcolor: 'white',
                  p: '6px'
                }}
              >
                <RefreshIcon fontSize="small" sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            size="small"
            onClick={handleExportExcel}
            disabled={loading || exportingExcel || products.length === 0}
            startIcon={
              exportingExcel ? (
                <CircularProgress size={16} />
              ) : (
                <PiMicrosoftExcelLogo style={{ color: '#107c41', fontSize: '1.2rem' }} />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#107c41',
              color: '#107c41',
              bgcolor: 'white',
              '&:hover': {
                borderColor: '#0b5c30',
                backgroundColor: 'rgba(16, 124, 65, 0.04)'
              }
            }}
          >
            Export Excel
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={handleExportPDF}
            disabled={loading || exportingPdf || products.length === 0}
            startIcon={
              exportingPdf ? (
                <CircularProgress size={16} />
              ) : (
                <MdPictureAsPdf style={{ color: '#d32f2f', fontSize: '1.2rem' }} />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#d32f2f',
              color: '#d32f2f',
              bgcolor: 'white',
              '&:hover': {
                borderColor: '#9a0007',
                backgroundColor: 'rgba(211, 47, 47, 0.04)'
              }
            }}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Content / Table */}
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={40} thickness={4} />
            <Typography variant="body2" sx={{ mt: 2, color: '#718096' }}>
              Loading unconfigured parent products...
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ flex: 1, maxHeight: '55vh', overflow: 'auto' }}>
            <Table stickyHeader size="small" aria-label="unassigned parent products table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#edf2f7', fontWeight: 700, color: '#2d3748', width: 60, textAlign: 'center' }}>
                    #
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#edf2f7', fontWeight: 700, color: '#2d3748', width: 180 }}>
                    Product Code
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#edf2f7', fontWeight: 700, color: '#2d3748' }}>
                    Product Name
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 6, color: '#718096' }}>
                      {searchQuery
                        ? 'No unconfigured parent products match your search.'
                        : 'No unconfigured parent products found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedRows.map((product, index) => {
                    return (
                      <TableRow
                        key={product._id || product.parentProductCode}
                        hover
                        sx={{
                          '&:nth-of-type(even)': { bgcolor: '#fbfcfd' },
                          '&:hover': { bgcolor: '#f0f7ff !important' }
                        }}
                      >
                        <TableCell sx={{ color: '#718096', fontSize: '0.85rem', textAlign: 'center' }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#1a365d', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                          {product.parentProductCode}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500, color: '#2d3748', fontSize: '0.9rem' }}>
                          {product.parentProductName}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      {/* Footer with Pagination and Close */}
      <DialogActions
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f5f7fa',
          borderTop: '1px solid #e0e0e0'
        }}
      >
        <Typography variant="body2" sx={{ color: '#718096', pl: 1 }}>
          Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'record' : 'records'}
          {searchQuery && ` (filtered from ${products.length})`}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TablePagination
            component="div"
            count={filteredProducts.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              '.MuiTablePagination-toolbar': { minHeight: 40 },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.8rem', color: '#4a5568' }
            }}
          />
          <Button onClick={onClose} variant="contained" size="small" sx={{ textTransform: 'none', px: 3 }}>
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UnassignedParentProductsDialog;
